import { useCallback, useState, type CSSProperties } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import TextButton from 'LuxKit/Button/TextButton'
import LineMapIcon from 'LuxKit/Icons/line/LineMapIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import OfferLocationMap from 'components/OfferPage/NavigableOfferContent/OfferLocationMap'
import { hasMapCoordinates } from 'components/OfferPage/NavigableOfferContent/mapCoordinates'

const MAP_RATIO_WIDTH = 406
const MAP_RATIO_HEIGHT = 102
const SIDE_COLUMN_MAX_WIDTH = 370
const MAP_IMAGE_HEIGHT = Math.round(SIDE_COLUMN_MAX_WIDTH * MAP_RATIO_HEIGHT / MAP_RATIO_WIDTH)
const HEADER_MAP_MARKER_SCALE = 0.85
const MAP_WRAPPER_STYLE: CSSProperties = { height: '100%' }

const Card = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  border: ${rem(1)} solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.L};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
`

const MapFrame = styled.div`
  display: flex;
  flex: 1 1 auto;
  width: 100%;
  min-block-size: ${rem(MAP_IMAGE_HEIGHT)};
`

const VIEW_ON_MAP_ICON = <LineMapIcon />

const LocationLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${rem(12)};
  padding: ${rem(12)} ${rem(16)};
`

const LocationText = styled.div`
  min-width: 0;
`

interface Props {
  offer: App.HotelOffer
}

function FlashDesktopOfferHeaderLocation(props: Props) {
  const { offer } = props
  const intl = useIntl()
  const property = offer.property
  const location = [offer.locationHeading, offer.locationSubheading].filter(Boolean).join(', ')
  const hasMap = hasMapCoordinates(property)
  const [openRequestId, setOpenRequestId] = useState(0)
  const viewOnMapLabel = intl.formatMessage({
    id: 'SHqf1gU73jLQ7S4mXqOB',
    defaultMessage: 'View on map',
    description: 'Button on the desktop flash offer header location bar that opens the interactive property map',
  })
  const handleViewOnMap = useCallback(() => {
    setOpenRequestId(current => current + 1)
  }, [])

  if (!location && !hasMap) {
    return null
  }

  return (
    <Card>
      {location && (
        <LocationLabel>
          <LocationText>
            <BodyText variant="medium" colour="light-foreground-alt">{location}</BodyText>
          </LocationText>
          {hasMap && (
            <TextButton
              kind="tertiary"
              size="small"
              endIcon={VIEW_ON_MAP_ICON}
              onClick={handleViewOnMap}
            >
              {viewOnMapLabel}
            </TextButton>
          )}
        </LocationLabel>
      )}
      {hasMap && (
        <MapFrame>
          <OfferLocationMap
            offer={offer}
            height={MAP_IMAGE_HEIGHT}
            openRequestId={openRequestId > 0 ? openRequestId : undefined}
            showFullscreenButton={false}
            staticMarkerScale={HEADER_MAP_MARKER_SCALE}
            wrapperStyle={MAP_WRAPPER_STYLE}
          />
        </MapFrame>
      )}
    </Card>
  )
}

export default FlashDesktopOfferHeaderLocation
